import { useState } from "react";
import { reportsApi } from "../services/reportsApi";
import { businessAnalyticsApi } from "../services/businessApi";
import { useFeatureTheme } from "../context/FeatureThemeContext";
import { useToast } from "../context/ToastContext";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { LockedFeature } from "../components/common/LockedFeature";
import { WatermarkSelect } from "../components/print/WatermarkSelect";
import { openPrintWindow } from "../utils/printWindow";
import { formatDateOnly } from "../services/hiveActions";
import { money } from "../utils/format";
import { StandardReport } from "../components/reports/StandardReport";

const REG_TYPES = ["Treatment", "Loss", "Annual", "Other"];


export function ReportsPage() {
  const { hasFeature } = useFeatureTheme();
  const { notify } = useToast();
  const [regType, setRegType] = useState("Annual");
  const [year, setYear] = useState(new Date().getFullYear());
  const [regResult, setRegResult] = useState(null);
  const [yearEndResult, setYearEndResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regWatermark, setRegWatermark] = useState("None");
  const [seasonWatermark, setSeasonWatermark] = useState("None");

  async function generateRegulatory() {
    setBusy(true);
    try {
      const r = await reportsApi.regulatory(regType, year);
      setRegResult(r.data);
      notify("Regulatory report generated", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not generate report", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function generateYearEnd() {
    setBusy(true);
    try {
      const r = await reportsApi.yearEnd(year);
      setYearEndResult(r);
      notify("Year-end report generated", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not generate report", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(kind, watermark) {
    setBusy(true);
    try {
      await reportsApi.pdf(kind, year, watermark);
    } catch (err) {
      notify(err.apiMessage || "Could not generate PDF", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function printSeasonReport() {
    setBusy(true);
    try {
      const { pnl } = await businessAnalyticsApi.pnl(year);
      const maxValue = Math.max(1, ...pnl.flatMap((r) => [r.income, r.expenses]));

      const bar = (value, color) => {
        const pct = Math.min(100, (value / maxValue) * 100);
        return `<div style="background:${color};height:14px;width:${pct}%;border-radius:3px;"></div>`;
      };

      const seasonRows = pnl
        .map(
          (r) => `<tr>
            <td>${r.season} ${r.year}</td>
            <td>$${r.income.toFixed(2)}<br/>${bar(r.income, "#2e8b57")}</td>
            <td>$${r.expenses.toFixed(2)}<br/>${bar(r.expenses, "#d64545")}</td>
            <td>$${r.profit.toFixed(2)}</td>
          </tr>`
        )
        .join("");

      const harvestRows = (yearEndResult?.harvests_by_type || [])
        .map((h) => `<tr><td>${h.harvest_type}</td><td>${h.total_quantity} ${h.unit}</td></tr>`)
        .join("");

      const body = `
        <h1>Season Report ${year}</h1>
        <div class="meta">Generated ${new Date().toLocaleDateString()}</div>
        <h2>Income / Expenses / Profit by Season</h2>
        <table>
          <thead><tr><th>Season</th><th>Income</th><th>Expenses</th><th>Profit</th></tr></thead>
          <tbody>${seasonRows || `<tr><td colspan="4">No data for ${year}</td></tr>`}</tbody>
        </table>
        ${harvestRows ? `<h2>Harvests</h2><table><thead><tr><th>Type</th><th>Total</th></tr></thead><tbody>${harvestRows}</tbody></table>` : ""}
      `;

      openPrintWindow(`Season Report ${year}`, body, { watermark: seasonWatermark });
    } catch (err) {
      notify(err.apiMessage || "Could not build season report", "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Reports</h1>

      {/* The two standard reports (#316), above the annual ones.
          Everything that existed before was ANNUAL and on demand, which
          is no use to somebody doing their books in July -- so the
          period-based reports lead.

          Each is its own surface with one primary and a menu, which is
          what keeps this page from becoming the 10-18 button screen
          #316 predicted. A third report costs one card. */}
      <StandardReport
        title="Revenue"
        description="Sales, payments received, and what is still owed. Figures come from
                     the price frozen on each delivery, so a later price change cannot
                     restate a past quarter."
        enabled={hasFeature("HoneyBusiness")}
        featureLabel="Revenue Reporting"
        previewText="See sales, payments and outstanding balances by month and by vendor."
        slug="revenue"
        scheduleLabel="Revenue"
        onSchedule={(cadence) => reportsApi.schedule("Revenue", cadence)}
        onGenerate={(start, end) => reportsApi.revenue(start, end)}
        onPdf={(start, end) => reportsApi.revenuePdf(start, end)}
        sections={[
          {
            key: "by_month", label: "By month", rowKey: "month",
            columns: [
              { key: "month", header: "Month" },
              { key: "bottles", header: "Jars" },
              { key: "sales_usd", header: "Sales", render: (r) => money(r.sales_usd) },
            ],
          },
          {
            key: "by_vendor", label: "By vendor", rowKey: "vendor",
            columns: [
              { key: "vendor", header: "Vendor" },
              { key: "bottles", header: "Jars" },
              { key: "sales_usd", header: "Sales", render: (r) => money(r.sales_usd) },
            ],
          },
        ]}
      />

      <StandardReport
        title="Hive activity"
        description="Inspections by site, requeenings, and how old your queens are."
        enabled={hasFeature("QueenManagement")}
        featureLabel="Hive Activity Reporting"
        previewText="See inspections done by site, requeening history and queen ages."
        slug="hive-activity"
        scheduleLabel="Hive activity"
        onSchedule={(cadence) => reportsApi.schedule("HiveActivity", cadence)}
        onGenerate={(start, end) => reportsApi.hiveActivity(start, end)}
        /* CSV only, deliberately. #316's rule is PDF for what a third
           party reads -- nobody shows an inspector a queen-age report,
           so a PDF here would be work for a use case that does not
           exist. */
        onPdf={null}
        sections={[
          {
            key: "by_site", label: "By site", rowKey: "site",
            columns: [
              { key: "site", header: "Site" },
              { key: "hives", header: "Hives" },
              { key: "inspections", header: "Inspections" },
              { key: "last_action", header: "Last action",
                render: (r) => (r.last_action ? formatDateOnly(r.last_action) : "--") },
            ],
          },
          {
            key: "requeenings", label: "Requeenings", rowKey: (r) => `${r.replaced_date}-${r.hive_number}`,
            columns: [
              { key: "replaced_date", header: "When", render: (r) => formatDateOnly(r.replaced_date) },
              { key: "site", header: "Site" },
              { key: "hive_number", header: "Hive" },
              { key: "reason", header: "Reason", render: (r) => r.reason || "--" },
            ],
          },
          {
            key: "active_queens", label: "Queens in service", rowKey: (r) => `${r.colour}-${r.birth_year}`,
            columns: [
              { key: "colour", header: "Marking" },
              { key: "birth_year", header: "Born" },
              { key: "genetic_line", header: "Line", render: (r) => r.genetic_line || "--" },
              { key: "origin", header: "Origin", render: (r) => r.origin || "--" },
            ],
          },
        ]}
      />

      <LockedFeature enabled={hasFeature("Regulatory")} featureLabel="Regulatory Reports" previewText="Generate treatment summaries and loss reports for regulatory submission.">
        <Card title="Regulatory Reports" className="section-spacing">
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "var(--space-4)" }}>
            <label>Report type
              <select id="reports-page-reg-type" name="reg-type" value={regType} onChange={(e) => setRegType(e.target.value)} className="touch-target" style={{ display: "block" }}>
                {REG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label>Year
              <input id="reports-page-year" name="year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="touch-target" style={{ display: "block", width: 100 }} />
            </label>
            <Button onClick={generateRegulatory} disabled={busy}>Generate</Button>
            <div style={{ width: 140 }}>
              <WatermarkSelect value={regWatermark} onChange={setRegWatermark} />
            </div>
            <Button variant="secondary" onClick={() => downloadPdf("Regulatory", regWatermark)} disabled={busy}>PDF Export</Button>
          </div>
          {regResult && <pre style={{ whiteSpace: "pre-wrap", background: "var(--color-bg-subtle)", padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}>{JSON.stringify(regResult, null, 2)}</pre>}
        </Card>
      </LockedFeature>

      <LockedFeature enabled={hasFeature("BusinessAdvanced")} featureLabel="Year-End Report" previewText="Full season summary with income, expenses, and harvest totals.">
        <Card title="Year-End Report" className="section-spacing">
          <div style={{ display: "flex", gap: "var(--space-3)", marginBottom: "var(--space-4)", flexWrap: "wrap", alignItems: "flex-end" }}>
            <Button onClick={generateYearEnd} disabled={busy}>Generate Year-End Summary</Button>
            <div style={{ width: 140 }}>
              <WatermarkSelect value={seasonWatermark} onChange={setSeasonWatermark} />
            </div>
            <Button variant="secondary" onClick={() => downloadPdf("YearEnd", seasonWatermark)} disabled={busy}>PDF Export</Button>
            <Button variant="secondary" onClick={printSeasonReport} disabled={busy}>Print Season Report</Button>
          </div>
          {yearEndResult && (
            <div>
              <p>Total Income: ${yearEndResult.total_income.toFixed(2)}</p>
              <p>Total Expenses: ${yearEndResult.total_expenses.toFixed(2)}</p>
              <p>Net Profit: ${yearEndResult.net_profit.toFixed(2)}</p>
            </div>
          )}
        </Card>
      </LockedFeature>

      <Card title="Custom Report Builder">
        <p style={{ color: "var(--color-text-muted)" }}>
          A fully custom date-range/section picker would need a dedicated backend endpoint (api/functions/reports.py currently
          only supports fixed Regulatory/YearEnd/HealthSummary report kinds via /api/reports/pdf). For now, use the Regulatory
          or Year-End generators above with the year you need.
        </p>
      </Card>
    </div>
  );
}
